//@flow

import React from "react"
import { connect } from "react-redux"
import { FormattedMessage, injectIntl } from "react-intl"
import { TweenMax, Cubic } from "gsap"
import SVGLoader from "../../UI/SVGLoader"
import Title from "../Shared/Title"
import TitleIcon from "../Shared/TitleIcon"
import { ZoomableBackgroundContainer } from "../../UI/BackgroundContainer"
import CTA from "../Shared/CTA"
import iconLungs from "../Icons/iconLungs.svg"
import { extendSession } from "../redux"
import Body from "./Body"
import Navigation from "./Navigation"
import Popup from "./Popup"
import {
  getContainerCoordinate,
  currentScaleFactor
} from "../FixedResolutionContainer"

import { Intro, TitleContent } from "./UI"

import background from "./background.jpg"

import type { IntlShape } from "react-intl"
import type { CoordinateConversion } from "../FixedResolutionContainer"
import type { BodyPart, HotspotID } from "./types"

type State = {
  currentBodyPart: BodyPart,
  currentHotspot: ?HotspotID,
  anchorX: number,
  anchorY: number
}

type Props = {
  intl: IntlShape,
  getContainerCoordinate: CoordinateConversion,
  extendSession: Function
}

const popupPositions: {
  [HotspotID]: { x: number, y: number, width: number }
} = {
  "neuro.one": { x: 78, y: 88, width: 658 },
  "neuro.two": { x: 1180, y: 238, width: 468 },
  "neuro.three": { x: 58, y: 318, width: 458 },
  "cardio.one": { x: 78, y: 88, width: 658 },
  "cardio.two": { x: 1120, y: 198, width: 508 },
  "cardio.three": { x: 78, y: 488, width: 658 },
  "respiratory.one": { x: 78, y: 98, width: 658 },
  "respiratory.two": { x: 128, y: 208, width: 638 },
  "respiratory.three": { x: 960, y: 408, width: 608 }
}

class BodyView extends React.Component<Props, State> {
  introText: Array<HTMLElement>
  animations: Array<any>
  popup: ?HTMLElement

  constructor(props: Props) {
    super(props)
    this.introText = []
    this.state = {
      currentBodyPart: "home",
      currentHotspot: null,
      anchorX: 0,
      anchorY: 0
    }
    this.popup = null
  }

  componentDidMount = () => {
    TweenMax.set(this.introText, { y: "-=40", opacity: 0 })
    TweenMax.set(this.popup, { y: 20, opacity: 0, ease: Cubic.easeOut })
    this.showIntro()
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { extendSession } = this.props
    const { currentBodyPart, currentHotspot } = this.state
    if (prevState.currentBodyPart !== this.state.currentBodyPart) {
      if (currentBodyPart === "home") {
        this.showIntro()
      } else {
        this.hideIntro()
      }
      extendSession()
    }
    if (prevState.currentHotspot !== currentHotspot) {
      currentHotspot ? this.revealPopup() : this.hidePopup()
      extendSession()
    }
  }

  handleBackgroundClick = (e: Event) => {
    const { currentBodyPart, currentHotspot } = this.state
    // Dismiss popup and then dismiss zoom.
    if (currentHotspot !== null) {
      this.handleSelectHotspot(null, null, null)
    } else if (currentBodyPart !== "home") {
      this.setState(() => ({ currentBodyPart: "home" }))
    }
  }

  showIntro = () => {
    const introText = this.introText.filter(el => el !== null)
    this.interruptAnimations(this.animations)
    this.animations = TweenMax.staggerTo(
      introText,
      0.35,
      {
        y: "0",
        opacity: 1,
        ease: Cubic.easeOut
      },
      0.15
    )
  }

  interruptAnimations = (animations: ?(TweenMax[])) => {
    if (animations) {
      for (let animation of animations) {
        animation.kill()
      }
    }
  }

  hideIntro = () => {
    const introText = this.introText.filter(el => el !== null)
    this.interruptAnimations(this.animations)
    this.animations = TweenMax.staggerTo(
      introText,
      0.25,
      {
        y: "40px",
        opacity: 0,
        ease: Cubic.easeOut
      },
      0.15
    )
  }

  handleSelectBodyPart = (currentBodyPart: BodyPart) => {
    this.setState(() => ({ currentBodyPart, currentHotspot: null }))
    this.hidePopup()
  }

  handleSelectHotspot = (
    id: ?HotspotID,
    anchorX: ?number,
    anchorY: ?number
  ) => {
    if (id) {
      this.setState(() => ({
        currentHotspot: id,
        anchorX: anchorX || this.state.anchorX,
        anchorY: anchorY || this.state.anchorY
      }))
    } else {
      this.setState(() => ({ currentHotspot: null }))
    }
  }

  revealPopup = () => {
    if (this.popup) {
      this.hidePopup()
      TweenMax.to(this.popup, 0.35, { y: 0, opacity: 1, ease: Cubic.easeOut })
    }
  }

  hidePopup = () => {
    if (this.popup) {
      TweenMax.set(this.popup, {
        y: 20,
        opacity: 0,
        ease: Cubic.easeOut
      })
    }
  }

  render() {
    const { intl: { formatMessage }, getContainerCoordinate } = this.props
    const { currentBodyPart, currentHotspot, anchorX, anchorY } = this.state
    const anchorPoint = getContainerCoordinate({ x: anchorX, y: anchorY })
    const popupPosition = currentHotspot
      ? popupPositions[currentHotspot]
      : { x: 0, y: 0, width: 600 }

    return (
      <ZoomableBackgroundContainer
        imgSrc={background}
        flex={true}
        zoom={currentBodyPart !== "home"}
        onClick={this.handleBackgroundClick}
      >
        <Intro>
          <TitleIcon innerRef={el => this.introText.push(el)}>
            <SVGLoader
              path={iconLungs}
              height="100%"
              width="100%"
              fill="#fff"
            />
          </TitleIcon>
          <div>
            <Title innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.body.title" />
            </Title>
            <TitleContent innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.body.intro" />
            </TitleContent>
            <CTA
              direction="right"
              message={formatMessage({ id: "kiosk.body.cta" })}
              appearance="light"
              innerRef={el => this.introText.push(el)}
              arrowFill="#25beae"
            />
          </div>
        </Intro>
        <Navigation
          appearAfter={0.5}
          currentBodyPart={currentBodyPart}
          onSelectBodyPart={this.handleSelectBodyPart}
        />
        <Body
          currentBodyPart={currentBodyPart}
          currentHotspot={currentHotspot}
          onSelectHotspot={this.handleSelectHotspot}
        />
        <Popup
          innerRef={el => (this.popup = el)}
          {...popupPosition}
          anchorPoint={anchorPoint}
          headline={`kiosk.body.hotspots.${currentHotspot ||
            "neuro.one"}.headline`}
          description={`kiosk.body.hotspots.${currentHotspot ||
            "neuro.one"}.description`}
        />
      </ZoomableBackgroundContainer>
    )
  }
}

export default injectIntl(
  connect(
    state => ({
      getContainerCoordinate: getContainerCoordinate(state),
      currentScaleFactor: currentScaleFactor(state)
    }),
    { extendSession }
  )(BodyView)
)



// WEBPACK FOOTER //
// ./src/Kiosk/BodyView/BodyView.js